import { useCallback, useEffect, useRef, useState, type HTMLAttributes, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/common.utils";

interface FilterChipItem {
  label: ReactNode;
  value: string;
}

interface FilterChipsProps extends HTMLAttributes<HTMLDivElement> {
  items: FilterChipItem[];
  value: string;
  onValueChange: (value: string) => void;
  itemClassName?: string;
  testIdPrefix?: string;
  scrollableOnMobile?: boolean;
}

export default function FilterChips({ items, value, onValueChange, className, itemClassName, testIdPrefix = "filter", scrollableOnMobile = false, ...props }: FilterChipsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({ previous: false, next: false });

  const updateScrollState = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    const maxScroll = element.scrollWidth - element.clientWidth;
    setScrollState({ previous: element.scrollLeft > 2, next: element.scrollLeft < maxScroll - 2 });
  }, []);

  useEffect(() => {
    if (!scrollableOnMobile) return undefined;
    const element = scrollRef.current;
    if (!element) return undefined;
    updateScrollState();
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(element);
    return () => observer.disconnect();
  }, [items, scrollableOnMobile, updateScrollState]);

  const move = (direction: 1 | -1) => {
    scrollRef.current?.scrollBy({ left: direction * Math.max(180, scrollRef.current.clientWidth * 0.7), behavior: "smooth" });
  };

  const chips = items.map((item) => {
    const active = value === item.value;
    return (
      <Button variant="seamless"
        type="button"
        key={item.value || "all"}
        onClick={(event) => {
          onValueChange(item.value);
          if (scrollableOnMobile) event.currentTarget.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
        }}
        className={cn(
          "shrink-0 rounded-[8px] border px-3.5 py-2 text-xs font-medium outline-none transition-colors",
          active ? "border-primary bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground" : "border-dash-border bg-white text-dash-muted hover:border-dash-ink/20 hover:bg-dash-hover hover:text-dash-ink",
          itemClassName
        )}
        aria-pressed={active}
        data-testid={`${testIdPrefix}-${item.value || "all"}`}
      >
        {item.label}
      </Button>
    );
  });

  if (scrollableOnMobile) {
    return (
      <div className="flex w-full min-w-0 items-center gap-1.5" {...props}>
        <Button type="button" variant="dashboardOutline" size="icon" onClick={() => move(-1)} disabled={!scrollState.previous} className="shrink-0 sm:hidden" aria-label="Show previous filters">
          <ChevronLeft />
        </Button>
        <div ref={scrollRef} onScroll={updateScrollState} className={cn("flex min-w-0 flex-1 snap-x gap-1.5 overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:flex-wrap sm:overflow-visible", className)}>
          {chips}
        </div>
        <Button type="button" variant="dashboardOutline" size="icon" onClick={() => move(1)} disabled={!scrollState.next} className="shrink-0 sm:hidden" aria-label="Show next filters">
          <ChevronRight />
        </Button>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-wrap gap-1.5", className)} {...props}>
      {chips}
    </div>
  );
}
